import { useCallback, useEffect, useState } from "react";
import type { SettingsResponse } from "../../shared/types";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";
import { useSetup } from "./SetupContext";

export type Run = (fn: () => Promise<SettingsResponse>, ok?: string | ((d: SettingsResponse) => string | null)) => Promise<boolean>;

/**
 * The business's settings for setup and Settings: read once, and swapped for what each save sends back. Every save
 * also hands setup its new state, so the top bar's pill moves the moment a step is done.
 */
export function useSettingsData(): {
  data: SettingsResponse | null;
  failed: boolean;
  run: Run;
  setData: (d: SettingsResponse) => void;
} {
  const [data, setLocal] = useState<SettingsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const toast = useToast();
  const { set: setSetup } = useSetup();

  const setData = useCallback(
    (d: SettingsResponse) => {
      setLocal(d);
      setSetup(d.setup);
    },
    [setSetup],
  );

  useEffect(() => {
    let alive = true;
    api.settings
      .get()
      .then((d) => alive && setData(d))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [setData]);

  const run = useCallback<Run>(
    async (fn, ok) => {
      try {
        const d = await fn();
        setData(d);
        const said = typeof ok === "function" ? ok(d) : ok;
        if (said) toast(said);
        return true;
      } catch (e) {
        // A plan-limit 402 already shows its one line.
        if (e instanceof ApiError && e.upgrade) return false;
        toast({ text: e instanceof ApiError ? e.message : "Something went wrong.", icon: "info" });
        return false;
      }
    },
    [setData, toast],
  );

  return { data, failed, run, setData };
}
